/**
 * Réservations › one équipement — what it is, where it is, and its day.
 *
 * The day lives in the URL (`?date=YYYY-MM-DD`, org-local) so a shared link
 * or a refresh lands on the same day; with none, or one already behind the
 * org's clock, it opens on today. A date beyond the amenity's advance limit
 * is pulled back to the last bookable day rather than shown as bookable.
 *
 * After a booking the day view gives way to a short confirmation: « Inviter
 * des visiteurs » for a confirmed booking (the invite form picks the booking
 * up from `bookingId`), or « Demande envoyée » while an admin must validate.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { ChevronLeft, CircleCheck, Clock, MapPin } from "lucide-react";
import { z } from "zod/v4";

import { Button } from "@gately/ui/button";
import { Card } from "@gately/ui/card";
import { EmptyState } from "@gately/ui/empty-state";
import { StatusBadge } from "@gately/ui/status-badge";
import { DATE_KEY_RE } from "@gately/validators";

import { TEMPLATE_ICONS } from "~/components/booking/amenity-icons";
import { BookingDayView } from "~/components/booking/BookingDayView";
import {
  addDaysToKey,
  amenityCapacityLabel,
  amenityModeLabel,
  todayKey,
} from "~/lib/booking-format";
import { orpc } from "~/lib/orpc";
import { useOrgTimeZone } from "~/lib/use-me";
import { resolvePermissions } from "~/lib/use-permissions";

const searchSchema = z.object({
  date: z.string().regex(DATE_KEY_RE).optional().catch(undefined),
});

export const Route = createFileRoute("/_dashboard/reservations/$amenityId")({
  validateSearch: (s) => searchSchema.parse(s),
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).hasAmenities) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: AmenityDayPage,
});

interface Booked {
  id: string;
  status: "pending" | "confirmed";
}

function AmenityDayPage() {
  const { amenityId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const timeZone = useOrgTimeZone();
  const [booked, setBooked] = useState<Booked | null>(null);

  const query = useQuery(
    orpc.amenity.get.queryOptions({ input: { id: amenityId } }),
  );
  const amenity = query.data;

  const today = todayKey(timeZone);
  const lastDay = amenity
    ? addDaysToKey(today, amenity.maxAdvanceDays)
    : undefined;
  const wanted = search.date && search.date >= today ? search.date : today;
  const date = lastDay && wanted > lastDay ? lastDay : wanted;

  function renderReady() {
    if (!amenity) return null;
    const Icon = TEMPLATE_ICONS[amenity.template];
    return (
      <>
        <div className="mb-6 flex items-start gap-4">
          <span className="bg-muted text-muted-foreground flex size-12 shrink-0 items-center justify-center rounded-xl">
            <Icon className="size-6" />
          </span>
          <div className="min-w-0 space-y-2">
            <h1 className="text-foreground text-2xl font-semibold tracking-[-0.02em]">
              {amenity.name}
            </h1>
            <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              {amenity.locationLabel ? (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="size-3.5" />
                  {amenity.locationLabel}
                </span>
              ) : null}
              <span>{amenityCapacityLabel(amenity.capacity)}</span>
              <span>{amenityModeLabel(amenity)}</span>
              {amenity.approval === "required" ? (
                <StatusBadge tone="info">Validation requise</StatusBadge>
              ) : null}
            </div>
            {amenity.description ? (
              <p className="max-w-2xl text-sm whitespace-pre-line">
                {amenity.description}
              </p>
            ) : null}
            {amenity.directions ? (
              <p className="text-muted-foreground max-w-2xl text-sm whitespace-pre-line">
                <span className="text-foreground font-medium">Accès : </span>
                {amenity.directions}
              </p>
            ) : null}
          </div>
        </div>

        {booked ? (
          <BookedPanel booked={booked} onAgain={() => setBooked(null)} />
        ) : (
          <div className="max-w-3xl">
            <BookingDayView
              amenity={amenity}
              date={date}
              timeZone={timeZone}
              onDateChange={(d) =>
                void navigate({ search: { date: d }, replace: true })
              }
              onBooked={setBooked}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="px-7 py-6">
      <Link
        to="/reservations"
        className="text-muted-foreground hover:text-foreground mb-3 inline-flex items-center gap-1 text-sm"
      >
        <ChevronLeft className="size-4" />
        Réserver
      </Link>
      {/* Data first: a failed REFETCH keeps `data` and must not blank it. */}
      {amenity ? (
        renderReady()
      ) : query.isError ? (
        <Card className="p-0">
          <EmptyState
            title="Équipement introuvable."
            description="Il a peut-être été retiré, ou il ne vous est pas ouvert."
          />
        </Card>
      ) : (
        <div className="space-y-4" aria-busy="true">
          <div className="h-12 w-64 animate-pulse rounded-lg bg-neutral-100" />
          <Card className="h-80 max-w-3xl animate-pulse bg-neutral-100/60" />
        </div>
      )}
    </div>
  );
}

function BookedPanel({
  booked,
  onAgain,
}: {
  booked: Booked;
  onAgain: () => void;
}) {
  const navigate = useNavigate();
  const confirmed = booked.status === "confirmed";
  return (
    <Card className="flex max-w-3xl flex-col items-center gap-4 px-6 py-10 text-center">
      <span
        className={
          confirmed
            ? "bg-success-tint text-success flex size-12 items-center justify-center rounded-full"
            : "bg-info-tint text-info flex size-12 items-center justify-center rounded-full"
        }
      >
        {confirmed ? (
          <CircleCheck className="size-6" />
        ) : (
          <Clock className="size-6" />
        )}
      </span>
      <div role="status" className="space-y-1">
        <h2 className="text-lg font-semibold">
          {confirmed ? "Réservation confirmée" : "Demande envoyée"}
        </h2>
        <p className="text-muted-foreground text-sm">
          {confirmed
            ? "Vous pouvez dès maintenant inviter vos visiteurs sur ce créneau."
            : "Vous serez notifié dès sa validation."}
        </p>
      </div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        <Button variant="ghost" onClick={onAgain}>
          Autre réservation
        </Button>
        <Button variant={confirmed ? "outline" : "default"} asChild>
          <Link to="/reservations/mes-reservations">Mes réservations</Link>
        </Button>
        {confirmed ? (
          <Button
            onClick={() =>
              void navigate({
                to: "/inviter",
                search: { bookingId: booked.id },
              })
            }
          >
            Inviter des visiteurs
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
